import { Box, Grid } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { getCSSPropsForButtonPadding } from '@/components/form/baseInputs/Button/styles';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareHiddenInput } from '@/components/form/formAwareInputs/FormAwareHiddenInput';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import { InputRepeaterSumRow } from '@/components/form/formAwareInputs/FormAwareInputRepeater/InputRepeaterSumRow';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { determineDefaultAssetClassId } from '@/modules/assets/utils';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useDefaultAssetClassId } from '@/modules/tenant/TenantDetailsContext/hooks/useDefaultAssetClassId';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { formatCurrency } from '@/utils/formatting/currency';

import { useAssetCategoryOptions } from './hooks/useAssetCategoryOptions';
import { StructuredAssetRowFields } from './StructuredAssetsSubform.fields';
import {
  Fields,
  Props,
  STRUCTURED_ASSETS_SUBFORM_NAMESPACE,
  SubformField,
} from './StructuredAssetsSubform.types';
import {
  getAssetValueSum,
  getOwnerAwareDefaultTaxSitus,
  shouldRequireStructuredAssetFields,
} from './StructuredAssetsSubform.utils';

export function UI(props: Props) {
  const { entityType, disabled, hideDescriptionInput, ownerPrincipalIds } =
    props;
  const { assetClassesById } = useTenantDetailsContext();
  const defaultAssetClassId = useDefaultAssetClassId();
  const { options: assetCategoryOptions, loading: formOptionsLoading } =
    useAssetCategoryOptions();

  const defaultCategoryId = useMemo(() => {
    return determineDefaultAssetClassId(
      entityType,
      defaultAssetClassId,
      assetClassesById
    );
  }, [defaultAssetClassId, entityType, assetClassesById]);

  const { primaryClients } = useUnguardedHouseholdDetailsContext();
  const defaultTaxSitus = useMemo(() => {
    return getOwnerAwareDefaultTaxSitus({
      primaryClients,
      ownerPrincipalIds,
    });
  }, [primaryClients, ownerPrincipalIds]);

  const { control, setValue, getValues } = useFormContext<Fields>();
  const assetsValues = useWatch({
    control,
    name: `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets`,
  });

  const assetValuesSum = useMemo(
    () => getAssetValueSum(assetsValues),
    [assetsValues]
  );

  // A single empty row may be submitted as "no assets", so don't require its
  // fields in that case.
  const assetFieldsRequired = useMemo(
    () => shouldRequireStructuredAssetFields(assetsValues),
    [assetsValues]
  );

  // expose the sum to consumers via the form state
  useEffect(() => {
    setValue(
      `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}._assetsValueSum` as const satisfies SubformField,
      assetValuesSum
    );
  }, [assetValuesSum, setValue]);

  // Backfill new (assetID-less) rows once primaryClients loads. Existing rows keep their loaded situs.
  const prevDefaultTaxSitus = usePrevious(defaultTaxSitus);
  useEffect(() => {
    if (!defaultTaxSitus || prevDefaultTaxSitus === defaultTaxSitus) return;
    const assets =
      getValues(`${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets`) ?? [];
    assets.forEach((asset, idx) => {
      const isNewRow = !asset.assetID;
      if (isNewRow && !asset.taxSitus) {
        setValue(
          `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets.${idx}.taxSitus` as const,
          defaultTaxSitus,
          { shouldDirty: false }
        );
        setValue(
          `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets.${idx}._originalTaxSitus` as const,
          defaultTaxSitus,
          { shouldDirty: false }
        );
      }
    });
  }, [defaultTaxSitus, prevDefaultTaxSitus, getValues, setValue]);

  return (
    <FormFieldsDisabledProvider
      isDisabled={Boolean(formOptionsLoading || disabled)}
    >
      <Grid container spacing={2} columns={12}>
        <Grid item sm={12}>
          <FormAwareHiddenInput<Fields>
            control={control}
            fieldName={
              `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.valuationId` as const satisfies SubformField
            }
          />
          <FormLayoutRow>
            <FormLayoutItem width={12}>
              {/*
                guarding the initial render of the repeater until we have the default category option
                so that we can initialize the empty value with a valid option
               */}
              {defaultCategoryId && (
                <FormAwareInputRepeater<Fields>
                  name={`${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.assets`}
                  emptyValue={{
                    categoryId: defaultCategoryId,
                    title: '',
                    value: null,
                    taxSitus: defaultTaxSitus,
                    _originalTaxSitus: defaultTaxSitus,
                  }}
                  control={control}
                  shouldInit
                  keepAtLeastOneRow
                  addAnotherItemText={`Add additional asset or account`}
                  addAnotherItemButtonProps={{
                    size: 'sm',
                    variant: 'transparent',
                    sx: {
                      ...getCSSPropsForButtonPadding('sm', false),
                    },
                  }}
                  rightFooterContent={
                    // Constrain to 75% of footer slot to align under the Value column.
                    <Box display="flex" justifyContent="flex-end">
                      <Box sx={{ width: '75%' }}>
                        <InputRepeaterSumRow
                          content={formatCurrency(assetValuesSum)}
                        />
                      </Box>
                    </Box>
                  }
                  render={(i: number) => (
                    <StructuredAssetRowFields
                      index={i}
                      options={assetCategoryOptions}
                      required={assetFieldsRequired}
                    />
                  )}
                />
              )}
            </FormLayoutItem>
          </FormLayoutRow>
          {!hideDescriptionInput && (
            <FormLayoutRow>
              <FormLayoutItem width={12}>
                <FormAwareTextInput<Fields>
                  control={control}
                  label="Description"
                  fieldName={
                    `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.description` as const satisfies SubformField
                  }
                  placeholder="Optional"
                  multiline
                  rows={3}
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
        </Grid>
      </Grid>
      <FormAwareHiddenInput<Fields>
        control={control}
        fieldName={
          `${STRUCTURED_ASSETS_SUBFORM_NAMESPACE}.accountId` as const satisfies SubformField
        }
      />
    </FormFieldsDisabledProvider>
  );
}
